import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  LayoutRectangle,
  PanResponder,
  PanResponderInstance,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';

const ANIMATION_DURATION_MS = 250;
const SCROLLBAR_WIDTH = 16;
const TAP_TIMEOUT_MS = 100;

interface Props {
  indexes: string[];
  isVisible: boolean;
  onSelect: (index: string) => void;
  onTouchEnd?: () => void;
  onTouchStart?: () => void;
}

export const IndexScrollbar: React.FC<Props> = ({
  indexes,
  isVisible,
  onSelect,
  onTouchEnd,
  onTouchStart,
}) => {
  const { appDimensions } = useLayoutContext();
  const [styles] = useStyles();

  const baseViewRef = useRef<View>(null);
  const contentViewRef = useRef<View>(null);
  const isAnimatingRef = useRef(false);
  const lastIndexRef = useRef<string | null>(null);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleValueRef = useRef(new Animated.Value(isVisible ? 1 : 0));
  const [baseOffsetStyle, setBaseOffsetStyle] = useState<ViewStyle | null>(
    null,
  );
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);
  const [panResponder, setPanResponder] = useState<PanResponderInstance>();

  useEffect(() => {
    Animated.timing(visibleValueRef.current, {
      duration: ANIMATION_DURATION_MS,
      toValue: isVisible ? 1 : 0,
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (finished) {
        isAnimatingRef.current = false;

        if (isVisible) {
          // Calculate layout after animation completes to ensure the base
          // view position has already updated
          contentViewRef.current?.measureInWindow((x, y, width, height) => {
            setLayout({
              height,
              width,
              x,
              y,
            });
          });
        } else {
          // Reset so it's re-calculated the next time it becomes visible
          setBaseOffsetStyle(null);
        }
      }
    });

    // Ensure the view is not animating before re-calculating insets otherwise
    // the measurement uses values from the previous animation and can cause
    // the scrollbar to render off-center
    if (isVisible && !isAnimatingRef.current) {
      // Calculate insets to extend this view to the top and bottom of the
      // screen so the scrollbar renders in the center
      baseViewRef.current?.measureInWindow((x, y, width, height) => {
        setBaseOffsetStyle({
          bottom: height + y - appDimensions.height,
          top: -y,
        });
      });
    }

    isAnimatingRef.current = true;
  }, [appDimensions.height, isVisible]);

  useEffect(() => {
    if (!layout) {
      return;
    }

    const selectIndex = (y: number) => {
      // Calculate the selected index based on the y-position of the gesture
      const top = y - layout.y;
      const nextIndex =
        indexes[Math.floor((top / layout.height) * indexes.length)];
      // Ensure the index has changed to prevent unnecessary calls while the
      // touch moves across the scrollbar
      if (nextIndex && nextIndex !== lastIndexRef.current) {
        onSelect(nextIndex);
        lastIndexRef.current = nextIndex;
      }
    };

    setPanResponder(
      PanResponder.create({
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: (_, gestureState) => {
          onTouchStart?.();

          // Register a timeout to detect if the user only taps an index
          tapTimeoutRef.current = setTimeout(() => {
            selectIndex(gestureState.y0);
          }, TAP_TIMEOUT_MS);
        },
        onPanResponderMove: (_, gestureState) => {
          if (tapTimeoutRef.current) {
            clearTimeout(tapTimeoutRef.current);
          }
          selectIndex(gestureState.moveY);
        },
        onPanResponderRelease: onTouchEnd,
        // Assume the touch ends if the responder terminates which can occur
        // if another responder takes over
        onPanResponderTerminate: onTouchEnd,
        onStartShouldSetPanResponder: () => true,
      }),
    );
  }, [indexes, layout, onSelect, onTouchEnd, onTouchStart]);

  return (
    <Animated.View
      ref={baseViewRef}
      style={[
        styles.base,
        baseOffsetStyle,
        {
          transform: [
            {
              translateX: visibleValueRef.current.interpolate({
                inputRange: [0, 1],
                outputRange: [SCROLLBAR_WIDTH, 0],
              }),
            },
          ],
        },
      ]}
      testID="index-scrollbar"
    >
      <View
        {...panResponder?.panHandlers}
        ref={contentViewRef}
        style={styles.content}
        testID="index-scrollbar-content"
      >
        <View style={styles.contentBackground} />
        {indexes.map((index) => (
          <Text key={index} style={[TYPOGRAPHY.xsmall, styles.index]}>
            {index}
          </Text>
        ))}
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    bottom: 0,
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  content: {
    alignContent: 'center',
    justifyContent: 'center',
    width: SCROLLBAR_WIDTH,
  },
  // Render as a separate view to simplify the selected index calculation
  contentBackground: {
    backgroundColor: `${theme.colors.white}bb`,
    bottom: -8,
    left: 0,
    position: 'absolute',
    right: 0,
    top: -8,
  },
  index: {
    ...FONTS.primaryBold,
    textAlign: 'center',
  },
}));
